import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { emailScheme, stringScheme } from 'src/utils/validation-schemes';
import { useUpdateUsersMeMutation } from 'src/services/usersService';
import { useNotification } from 'src/hooks/useNotification';

interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
}

interface UserInfoFormValues {
  first_name: string;
  last_name: string;
  email: string;
}

const schema = object({
  first_name: stringScheme({ required: true }).label('First name'),
  last_name: stringScheme({ required: true }).label('Last name'),
  email: emailScheme({ required: true }).label('Email'),
});

// Owns the "My Info" form: default values sourced from the current user,
// dirty-field diffing on submit, and the save/cancel side effects.
export const useUserInfoForm = (
  userInfo: UserInfo | undefined,
  onDone: () => void,
  onLoadingChange?: (isLoading: boolean) => void,
) => {
  const notification = useNotification();
  const [updateUserInfo, { isLoading, isSuccess }] = useUpdateUsersMeMutation();

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading]);

  const {
    control,
    trigger,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: { first_name: '', last_name: '', email: '' },
    resolver: formResolver(schema),
  });

  useEffect(() => {
    if (userInfo) {
      reset({
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        email: userInfo.email,
      });
    }
  }, [userInfo, reset]);

  useEffect(() => {
    if (isSuccess) {
      notification.success('Your information has been updated successfully.');
    }
  }, [isSuccess]);

  const submit = handleSubmit((values) => {
    const request: Partial<UserInfoFormValues> = (
      Object.keys(dirtyFields) as Array<keyof UserInfoFormValues>
    ).reduce((acc, key) => {
      if (dirtyFields[key]) {
        acc[key] = values[key];
      }
      return acc;
    }, {} as Partial<UserInfoFormValues>);

    updateUserInfo(request);
    onDone();
  });

  const cancel = () => {
    reset();
    onDone();
  };

  return { control, trigger, submit, cancel, isLoading };
};
